import { Accordion, Alert, Button, Group, Modal, Select, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { useMemo, useState } from 'react';
import {
  ErrorBoundaryInputBinding,
  ErrorBoundaryOutputAlias,
  ErrorBoundaryRecoveryPolicy,
  ErrorBoundaryRecoveryReceiptOutput,
  ErrorBoundaryRecoveryManifestOutput,
  ErrorBoundaryRecoveryCheckpoint,
  FailureHandlerStep,
} from '@/types/automationSteps.ts';
import StepFieldsAddButton from '@/pages/Editor/components/StepFieldsAddButton.tsx';
import StyledInputLabel from '@/components/StyledInputFields/StyledInputLabel.tsx';
import StepCommonFields from '@/pages/Editor/components/SharedFields/StepCommonFields.tsx';
import StepAdvancedConfigFields from '@/pages/Editor/components/SharedFields/StepAdvancedConfigFields.tsx';
import { FailureHandlerOutputAliasFields } from '@/pages/Editor/components/StepSidebar/StepForms/FailureHandlerStep/FailureHandlerOutputAliasFields.tsx';
import { FailureHandlerInputBindingFields } from '@/pages/Editor/components/StepSidebar/StepForms/FailureHandlerStep/FailureHandlerInputBindingFields.tsx';
import OutputPinButton from '@/pages/Editor/components/StepSidebar/OutputPinButton.tsx';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import { DEFAULT_DYNAMIC_DATA_INPUT } from '@/utils/constants.ts';
import { generateUid } from '@/utils/misc.ts';
import { protectedLeafOptions } from '@/pages/Editor/components/StepSidebar/StepForms/FailureHandlerStep/recoveryCheckpointOptions.ts';
import { findContinuationRoutingWarnings } from '@/pages/Editor/components/StepSidebar/StepForms/FailureHandlerStep/continuationRouting.ts';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';

type Props = {
  step: FailureHandlerStep;
};

type Fields = {
  recovery_policy: ErrorBoundaryRecoveryPolicy;
  protected_input_bindings: ErrorBoundaryInputBinding[];
  handler_input_bindings: ErrorBoundaryInputBinding[];
  protected_success_outputs: ErrorBoundaryOutputAlias[];
  recovery_outputs: ErrorBoundaryOutputAlias[];
  recovery_checkpoints: ErrorBoundaryRecoveryCheckpoint[];
  recovery_manifest_output: ErrorBoundaryRecoveryManifestOutput | null;
  recovery_receipt_output: ErrorBoundaryRecoveryReceiptOutput | null;
  recovery_continuation_outputs: ErrorBoundaryOutputAlias[];
};

export function FailureHandlerForm({ step }: Props) {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step });
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const [confirmStopMode, setConfirmStopMode] = useState(false);
  const successOutputs = fields.protected_success_outputs || [];
  const recoveryPorts = fields.recovery_outputs || [];
  const continuationOutputs = fields.recovery_continuation_outputs || [];
  const protectedInputs = fields.protected_input_bindings || [];
  const handlerInputs = fields.handler_input_bindings || [];
  const recoveryCheckpoints = fields.recovery_checkpoints || [];
  const recoveryPolicy = fields.recovery_policy || ErrorBoundaryRecoveryPolicy.RethrowOriginalFailure;
  const continuationRoutingWarning = useMemo(
    () => findContinuationRoutingWarnings(latestVersion?.steps || []).find((warning) => warning.boundaryStepId === step.step_id),
    [latestVersion?.steps, step.step_id],
  );
  const checkpointSourceOptions = protectedLeafOptions(step.children);
  const makeBinding = (): ErrorBoundaryInputBinding => ({
    output_id: generateUid(),
    name: '',
    data_input: DEFAULT_DYNAMIC_DATA_INPUT,
  });
  const makeAlias = (): ErrorBoundaryOutputAlias => ({
    output_id: generateUid(),
    name: '',
    child_data_output: DEFAULT_DYNAMIC_DATA_INPUT,
  });
  const makeCheckpoint = (): ErrorBoundaryRecoveryCheckpoint => ({
    checkpoint_id: generateUid(),
    name: '',
    source_step_id: '',
  });
  const applyRecoveryPolicy = (policy: ErrorBoundaryRecoveryPolicy) => {
    onChangeNoDelay({
      recovery_policy: policy,
      recovery_receipt_output:
        policy === ErrorBoundaryRecoveryPolicy.ContinueAfterRecovery
          ? fields.recovery_receipt_output || { output_id: generateUid(), name: 'Response receipt' }
          : null,
      recovery_continuation_outputs:
        policy === ErrorBoundaryRecoveryPolicy.ContinueAfterRecovery ? fields.recovery_continuation_outputs || [] : [],
    });
  };
  const hasContinuationConfiguration = !!fields.recovery_receipt_output || continuationOutputs.length > 0;

  return (
    <Stack gap="md">
      <Modal opened={confirmStopMode} onClose={() => setConfirmStopMode(false)} title="Remove continuation configuration" centered>
        <Stack gap="md">
          <Text size="sm">Stopping after recovery removes the response receipt and response outputs because they are valid only when downstream work continues.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmStopMode(false)}>Keep current configuration</Button>
            <Button color="red" onClick={() => { applyRecoveryPolicy(ErrorBoundaryRecoveryPolicy.RethrowOriginalFailure); setConfirmStopMode(false); }}>Stop after recovery</Button>
          </Group>
        </Stack>
      </Modal>
      <StepCommonFields step={step} nameLabel="Step Name" />
      <Alert color="orange" title="Error-handling behavior">
        <Text size="sm">
          Original steps run first. If they error or complete with errors, response steps run once,
          then either the original outcome is re-propagated or normal downstream work continues, according
          to the selected response policy. Use this for unexpected failures, not expected business routing or Retry.
          Pauses and cancellations do not trigger response steps.
        </Text>
      </Alert>
      <Stack gap="xs">
        <StyledInputLabel>Choose recovery outcome</StyledInputLabel>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {[
            {
              policy: ErrorBoundaryRecoveryPolicy.RethrowOriginalFailure,
              title: 'Stop after recovery',
              description: 'Preserve the original failure. Normal downstream work does not continue.',
            },
            {
              policy: ErrorBoundaryRecoveryPolicy.ContinueAfterRecovery,
              title: 'Continue after recovery',
              description: 'Route downstream work on the response receipt after recovery completes.',
            },
          ].map((outcome) => (
            <Button
              key={outcome.policy}
              variant={recoveryPolicy === outcome.policy ? 'light' : 'default'}
              color={outcome.policy === ErrorBoundaryRecoveryPolicy.ContinueAfterRecovery ? 'blue' : 'orange'}
              aria-pressed={recoveryPolicy === outcome.policy}
              h="auto"
              p="sm"
              styles={{ label: { display: 'block', textAlign: 'left', whiteSpace: 'normal' } }}
              onClick={() => {
                if (
                  outcome.policy === ErrorBoundaryRecoveryPolicy.RethrowOriginalFailure &&
                  hasContinuationConfiguration
                ) {
                  setConfirmStopMode(true);
                  return;
                }
                applyRecoveryPolicy(outcome.policy);
              }}
            >
              <Text fw={600} size="sm">{outcome.title}</Text>
              <Text size="xs" c="dimmed">{outcome.description}</Text>
            </Button>
          ))}
        </SimpleGrid>
      </Stack>
      <Alert color={recoveryPolicy === ErrorBoundaryRecoveryPolicy.ContinueAfterRecovery ? 'blue' : 'orange'} title="Current recovery behavior">
        <Text size="sm">{recoveryPolicy === ErrorBoundaryRecoveryPolicy.ContinueAfterRecovery
          ? 'A completed response can continue normal downstream work. Downstream consumers must route on the response receipt before using outcome-specific outputs.'
          : 'Response steps may complete, but the original protected failure remains terminal and normal downstream work does not continue.'}</Text>
      </Alert>
      {recoveryPolicy === ErrorBoundaryRecoveryPolicy.ContinueAfterRecovery && (
        <>
          <Alert color="blue" title="Continuation contract">
            <Text size="sm">
              Downstream steps receive a response receipt and must branch on whether original work succeeded or response steps completed.
              Response context and diagnostics stay inside the response scope.
            </Text>
            <Text size="sm" mt="xs">
              Validation can verify that a route receives the receipt, but cannot prove the route evaluates the receipt outcome correctly. Treat it as a required review item before publishing.
            </Text>
          </Alert>
          {continuationRoutingWarning && (
            <Alert color="yellow" title="Receipt routing needs review">
              <Text size="sm">
                {continuationRoutingWarning.unverifiedConsumerStepIds.length} configured downstream consumer{continuationRoutingWarning.unverifiedConsumerStepIds.length === 1 ? '' : 's'} use response-only output{continuationRoutingWarning.unverifiedConsumerStepIds.length === 1 ? '' : 's'} without a verifiable receipt-based Conditional route.
              </Text>
              <Text size="sm" mt="xs">
                Route each consumer through a Conditional branch that takes this response receipt as an input. This is a configuration review warning, not runtime validation.
              </Text>
            </Alert>
          )}
          {fields.recovery_receipt_output && (
            <Stack gap={0}>
              <Group gap="xs">
                <StyledInputLabel>{fields.recovery_receipt_output.name}</StyledInputLabel>
                <OutputPinButton step={step} outputId={fields.recovery_receipt_output.output_id} />
              </Group>
              <Text size="sm" c="dimmed">
                This typed receipt is always emitted in continue mode; it is the safe downstream control signal.
              </Text>
            </Stack>
          )}
          <Stack gap="xs">
            <StyledInputLabel>Response outputs</StyledInputLabel>
            <Text size="sm" c="dimmed">
              Expose selected direct response-step outputs to normal downstream steps only after response steps succeed. Keep response diagnostics local.
            </Text>
            {continuationOutputs.map((alias) => (
              <FailureHandlerOutputAliasFields
                key={alias.output_id}
                alias={alias}
                label="Response output"
                sourceScope="recovery"
                step={step}
                pinnable
                onChangeDelay={(updated) =>
                  onChange({
                    recovery_continuation_outputs: continuationOutputs.map((output) =>
                      output.output_id === updated.output_id ? updated : output,
                    ),
                  })
                }
                onChangeNoDelay={(updated) =>
                  onChangeNoDelay({
                    recovery_continuation_outputs: continuationOutputs.map((output) =>
                      output.output_id === updated.output_id ? updated : output,
                    ),
                  })
                }
                onDelete={() =>
                  onChangeNoDelay({
                    recovery_continuation_outputs: continuationOutputs.filter((output) => output.output_id !== alias.output_id),
                  })
                }
              />
            ))}
            <StepFieldsAddButton
              stepId={step.step_id}
              elementCount={continuationOutputs.length}
              onClick={() =>
                onChangeNoDelay({ recovery_continuation_outputs: [...continuationOutputs, makeAlias()] })
              }
            />
          </Stack>
        </>
      )}
      <Accordion variant="contained">
        <Accordion.Item value="advanced-recovery">
          <Accordion.Control>Advanced recovery configuration</Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
      <Text size="sm" c="dimmed">
        Response steps can use the automatically generated “{step.failure_event_output.name}” event output.
      </Text>
      <Stack gap="xs">
        <StyledInputLabel>Original inputs</StyledInputLabel>
        <Text size="sm" c="dimmed">
          Bind upstream datasets privately for original work. Response steps cannot read these ports.
        </Text>
        {protectedInputs.map((binding) => (
          <FailureHandlerInputBindingFields
            key={binding.output_id}
            binding={binding}
            label="Original input"
            step={step}
            onChange={(updated) =>
              onChangeNoDelay({
                protected_input_bindings: protectedInputs.map((item) =>
                  item.output_id === updated.output_id ? updated : item,
                ),
              })
            }
            onDelete={() =>
              onChangeNoDelay({
                protected_input_bindings: protectedInputs.filter((item) => item.output_id !== binding.output_id),
              })
            }
          />
        ))}
        <StepFieldsAddButton
          stepId={step.step_id}
          elementCount={protectedInputs.length}
          onClick={() => onChangeNoDelay({ protected_input_bindings: [...protectedInputs, makeBinding()] })}
        />
      </Stack>
      <Stack gap="xs">
        <StyledInputLabel>Response inputs</StyledInputLabel>
        <Text size="sm" c="dimmed">
          Bind only safe upstream context for response work. Original steps cannot read these ports.
        </Text>
        {handlerInputs.map((binding) => (
          <FailureHandlerInputBindingFields
            key={binding.output_id}
            binding={binding}
            label="Response input"
            step={step}
            onChange={(updated) =>
              onChangeNoDelay({
                handler_input_bindings: handlerInputs.map((item) =>
                  item.output_id === updated.output_id ? updated : item,
                ),
              })
            }
            onDelete={() =>
              onChangeNoDelay({
                handler_input_bindings: handlerInputs.filter((item) => item.output_id !== binding.output_id),
              })
            }
          />
        ))}
        <StepFieldsAddButton
          stepId={step.step_id}
          elementCount={handlerInputs.length}
          onClick={() => onChangeNoDelay({ handler_input_bindings: [...handlerInputs, makeBinding()] })}
        />
      </Stack>
      <Stack gap="xs">
        <StyledInputLabel>Original-success outputs</StyledInputLabel>
        <Text size="sm" c="dimmed">
          Expose selected original-work outputs to normal downstream steps only when all original work succeeds.
        </Text>
        {successOutputs.map((alias) => (
          <FailureHandlerOutputAliasFields
            key={alias.output_id}
            alias={alias}
            label="Success output"
            step={step}
            pinnable
            onChangeDelay={(updated) =>
              onChange({
                protected_success_outputs: successOutputs.map((output) =>
                  output.output_id === updated.output_id ? updated : output,
                ),
              })
            }
            onChangeNoDelay={(updated) =>
              onChangeNoDelay({
                protected_success_outputs: successOutputs.map((output) =>
                  output.output_id === updated.output_id ? updated : output,
                ),
              })
            }
            onDelete={() =>
              onChangeNoDelay({ protected_success_outputs: successOutputs.filter((output) => output.output_id !== alias.output_id) })
            }
          />
        ))}
        <StepFieldsAddButton
          stepId={step.step_id}
          elementCount={successOutputs.length}
          onClick={() => onChangeNoDelay({ protected_success_outputs: [...successOutputs, makeAlias()] })}
        />
      </Stack>
      <Stack gap="xs">
        <StyledInputLabel>Response context</StyledInputLabel>
        <Text size="sm" c="dimmed">
          Expose selected committed artifacts to response steps after a failure. Response logic should consult the generated
          response manifest before using a port.
        </Text>
        {recoveryPorts.map((alias) => (
          <FailureHandlerOutputAliasFields
            key={alias.output_id}
            alias={alias}
            label="Response context"
            step={step}
            onChangeDelay={(updated) =>
              onChange({
                recovery_outputs: recoveryPorts.map((output) =>
                  output.output_id === updated.output_id ? updated : output,
                ),
              })
            }
            onChangeNoDelay={(updated) =>
              onChangeNoDelay({
                recovery_outputs: recoveryPorts.map((output) =>
                  output.output_id === updated.output_id ? updated : output,
                ),
              })
            }
            onDelete={() => {
              const remainingPorts = recoveryPorts.filter((output) => output.output_id !== alias.output_id);
              onChangeNoDelay({
                recovery_outputs: remainingPorts,
                recovery_manifest_output:
                  remainingPorts.length > 0 || recoveryCheckpoints.length > 0 ? fields.recovery_manifest_output : null,
              });
            }}
          />
        ))}
        <StepFieldsAddButton
          stepId={step.step_id}
          elementCount={recoveryPorts.length}
          onClick={() =>
            onChangeNoDelay({
              recovery_outputs: [...recoveryPorts, makeAlias()],
              recovery_manifest_output: fields.recovery_manifest_output || {
                output_id: generateUid(),
                name: 'Response context manifest',
              },
            })
          }
        />
      </Stack>
      <Stack gap="xs">
        <StyledInputLabel>Response execution checkpoints</StyledInputLabel>
        <Text size="sm" c="dimmed">
          Reached means the platform created a matching original leaf StepRun in this error-handling episode. It is
          conservative entry evidence, not proof of an external effect or provider receipt. Conditional uses its selected
          branch; Retry and fan-out scopes use any-entered semantics. Checkpoints expose no output data or provider payload.
        </Text>
        {recoveryCheckpoints.map((checkpoint) => (
          <Group key={checkpoint.checkpoint_id} align="end" wrap="nowrap">
            <TextInput
              label="Checkpoint name"
              value={checkpoint.name}
              onChange={(event) =>
                onChange({
                  recovery_checkpoints: recoveryCheckpoints.map((item) =>
                    item.checkpoint_id === checkpoint.checkpoint_id ? { ...item, name: event.currentTarget.value } : item,
                  ),
                })
              }
            />
            <Select
              label="Original leaf step"
              data={checkpointSourceOptions}
              value={checkpoint.source_step_id || null}
              onChange={(source_step_id) =>
                onChangeNoDelay({
                  recovery_checkpoints: recoveryCheckpoints.map((item) =>
                    item.checkpoint_id === checkpoint.checkpoint_id ? { ...item, source_step_id: source_step_id || '' } : item,
                  ),
                })
              }
              searchable
              nothingFoundMessage="No eligible original leaf steps"
            />
            <Button
              variant="subtle"
              color="red"
              onClick={() =>
                {
                  const remainingCheckpoints = recoveryCheckpoints.filter(
                    (item) => item.checkpoint_id !== checkpoint.checkpoint_id,
                  );
                  onChangeNoDelay({
                    recovery_checkpoints: remainingCheckpoints,
                    recovery_manifest_output:
                      remainingCheckpoints.length > 0 || recoveryPorts.length > 0 ? fields.recovery_manifest_output : null,
                  });
                }
              }
            >
              Remove
            </Button>
          </Group>
        ))}
        <StepFieldsAddButton
          stepId={step.step_id}
          elementCount={recoveryCheckpoints.length}
          onClick={() =>
            onChangeNoDelay({
              recovery_checkpoints: [...recoveryCheckpoints, makeCheckpoint()],
              recovery_manifest_output: fields.recovery_manifest_output || {
                output_id: generateUid(),
                name: 'Response context manifest',
              },
            })
          }
        />
      </Stack>
      <StepAdvancedConfigFields step={step} />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
